import { useLocation, useNavigate } from 'react-router'
import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { Button, Typography } from 'shared/ui'
import { LINK_10DLC_LIMITS_EXCEEDED } from 'shared/constants/links.ts'
import { logger } from 'shared/lib'
import { DropdownCards, DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing'
import { ComplianceApi } from 'entities/Compliance'
import {
  type IResponseBillingDepositPrice,
  type IResponseBillingDepositPricePreview,
} from 'entities/Billing/api/types.ts'
import { StepsCommonLayout } from 'pages/settings/ui/StepsCommonLayout/StepsCommonLayout.tsx'
import { CompliancePath } from 'pages/settings/pages/compliance/route/compliancePath.ts'
import { SettingsPath } from 'pages/settings/route/settingsPath.ts'
import { PaymentInfo } from 'pages/settings/pages/compliance/pages/upgrade10DLCPackage/pages/summary/PaymentInfo/PaymentInfo.tsx'
import styles from './styles.module.scss'

export const Summary = observer(() => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const depositPrice = state as IResponseBillingDepositPrice
  const [depositPricePreview, setDepositPricePreview] =
    useState<IResponseBillingDepositPricePreview | null>(null)

  useEffect(() => {
    if (!depositPrice) {
      navigate(`/${SettingsPath.compliance.localNumbers.root}`)
    } else {
      BillingApi.getDepositPricePreview(depositPrice.id)
        .then(({ data }) => {
          setDepositPricePreview(data)
        })
        .catch(console.error)
    }
  }, [depositPrice])

  const { billingStore, subscriptionStore, complianceStore } = useEntitiesContext()
  const dropdownCardsStore = useLocalObservable(
    () =>
      new DropdownCardsStore(billingStore, subscriptionStore, {
        labelText: 'Payment method',
      })
  )
  const [loading, setLoading] = useState(false)
  const onUpgrade = async () => {
    try {
      setLoading(true)
      if (!dropdownCardsStore.activeId) return
      const { data } = await BillingApi.billingDepositReplenish({
        payment_method: dropdownCardsStore.activeId,
        price_id: depositPrice.id,
        type: 'price',
      })
      if (data) {
        const response = await ComplianceApi.upgradeBrand()
        await complianceStore.fetchComplianceServicesList()
        response && navigate(`/${SettingsPath.compliance.localNumbers.root}`)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('payment_method')) {
          dropdownCardsStore.setPaymentError('Please select a different card')
        }
        if (e.response?.data?.error_message?.includes('Brand must be on LOW_VOLUME_STANDARD')) {
          await complianceStore.fetchComplianceServicesList()
          navigate(`/${SettingsPath.compliance.localNumbers.root}`)
        }
      }
      logger.error(e)
    } finally {
      setLoading(false)
    }
  }
  return (
    <StepsCommonLayout
      progressTitle={'Order summary'}
      title={'Upgrade 10DLC package'}
      totalSteps={2}
      settingsStep={2}
      closeSetting={() => navigate('/' + CompliancePath.compliance.localNumbers.root)}
      decreaseStep={() => navigate(-1)}
      content={
        <>
          <div className={styles.content}>
            <div>
              <Typography ariaLabel={'desc'} variant={'body-md-regular'} color={'tertiary'}>
                These fees are set by the carrier to register your brand and use case. Salesmsg
                doesn&#39;t mark them up.&nbsp;
              </Typography>

              <Typography ariaLabel={'learnMore'} variant={'body-md-medium'} linkLight>
                <a href={LINK_10DLC_LIMITS_EXCEEDED} target='_blank' rel='noreferrer'>
                  Learn more
                </a>
              </Typography>
            </div>
            {depositPricePreview && <PaymentInfo depositPricePreview={depositPricePreview} />}
            <DropdownCards store={dropdownCardsStore} />
          </div>
          <div className={styles.actions}>
            <Button
              text={' Upgrade package'}
              onClick={onUpgrade}
              disabled={!depositPricePreview || !dropdownCardsStore.activeId}
              size={'large'}
              fullWidth
              loading={loading}
            />
          </div>
        </>
      }
    />
  )
})
